/**
 * My Points — tech-facing page (TECH-POINTS-PAGE.md).
 *
 * Three levels of disclosure: pay period (hero total + per-day bars) → day
 * rows → jobs → task lines. iPad-first: card language, 44pt targets, no
 * tables, no hover-only affordances. All data comes from the existing points
 * engine; grouping lives in ./points/model.ts (pure, unit-tested).
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PageSpinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import {
    ArrowLeft,
    Check,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    Clock,
    HelpCircle,
    Star,
    Wrench,
} from "lucide-react";
import { useFieldRedesign } from "@/lib/use-field-redesign";
import {
    POINTS_ASK_LABEL,
    POINTS_ASK_PROMPT,
    pointsAskMessage,
    pointsBarLabel,
    pointsExplainer,
    pointsJobStateCopy,
} from "./redesign-screens-copy";
import { saveThroughOutbox } from "@/lib/save-outbox";
import { appVersion } from "@/lib/app-version";
import { toast } from "sonner";
import {
    adjustmentsForPeriod,
    buildBars,
    buildDayModel,
    buildPeriodOptions,
    summarize,
    type ApiAdjustment,
    type ApiDayTotal,
    type ApiPointEvent,
    type DayRow,
    type JobGroup,
    type TaskStatus,
} from "./points/model";
import { useTeachUsed } from "@/teach/teach-state";
import { TeachIntro } from "@/teach/teach-card";
import { HowItWorks } from "@/components/how-it-works";

interface MeProfilePay {
    pay: { payPeriodDays: number; dailyPointsTarget: number | null };
}

/** Three-letter weekday for a bar label, from the UTC date the model uses. */
function shortWeekday(isoDay: string): string {
    const d = new Date(`${isoDay}T00:00:00Z`);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { weekday: "short", timeZone: "UTC" });
}

// ─── Status chips (tech language: provisional → "pending") ──────────────────

function StatusChip({ status }: { status: TaskStatus }) {
    if (status === "approved") {
        return (
            <Badge className="shrink-0 border-transparent bg-yellow-500 text-yellow-950 text-[10px] font-bold">
                <Check className="mr-0.5 h-3 w-3" /> approved
            </Badge>
        );
    }
    if (status === "pending") {
        return (
            <Badge
                variant="outline"
                className="shrink-0 border-amber-500 text-amber-600 dark:text-amber-400 text-[10px] font-bold"
            >
                <Clock className="mr-0.5 h-3 w-3" /> pending
            </Badge>
        );
    }
    return (
        <Badge variant="outline" className="shrink-0 border-destructive/60 text-destructive text-[10px] font-bold">
            rejected
        </Badge>
    );
}

// ─── Level 3: task lines inside a job ────────────────────────────────────────

function TaskLines({ job }: { job: JobGroup }) {
    return (
        <div className="mt-1 space-y-1 border-l-2 border-border pl-3">
            {job.tasks.map((t) => (
                <div key={t.id} className="flex items-baseline justify-between gap-2 text-sm">
                    <span
                        className={cn(
                            "min-w-0 flex-1",
                            t.status === "rejected" && "line-through text-muted-foreground",
                        )}
                    >
                        {t.label}
                        {t.quantity > 1 && <span className="text-foreground/60"> ×{t.quantity}</span>}
                        {t.status === "rejected" && t.rejectionReason && (
                            <span className="ml-1 text-xs text-destructive no-underline">— {t.rejectionReason}</span>
                        )}
                    </span>
                    <span
                        className={cn(
                            "shrink-0 font-mono tabular-nums",
                            t.status === "rejected" ? "line-through text-muted-foreground" : "font-semibold",
                        )}
                    >
                        {t.points} pt{t.points === 1 ? "" : "s"}
                    </span>
                </div>
            ))}
        </div>
    );
}

// ─── Level 2: a day row expanding to jobs ────────────────────────────────────

function DayCard({
    day,
    expanded,
    onToggle,
    showPendingHint,
    redesign,
}: {
    day: DayRow;
    expanded: boolean;
    onToggle: () => void;
    showPendingHint: boolean;
    /** #950 Y4 — plain words for what each job's points are doing. */
    redesign?: boolean;
}) {
    const navigate = useNavigate();
    const [openJobs, setOpenJobs] = useState<Set<string>>(new Set());

    if (day.isZero) {
        return (
            <div
                id={`day-${day.day}`}
                className="flex min-h-[44px] items-center justify-between px-4 py-2 text-sm text-muted-foreground"
            >
                <span>{day.label}</span>
                <span>— no points</span>
            </div>
        );
    }

    const toggleJob = (key: string) =>
        setOpenJobs((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });

    return (
        <Card id={`day-${day.day}`} className={cn(day.isToday && "border-primary/50")}>
            <CardContent className="p-0">
                <button
                    type="button"
                    onClick={onToggle}
                    className="flex min-h-[52px] w-full items-center gap-3 px-4 py-3 text-left"
                >
                    <div className="min-w-0 flex-1">
                        <span className="text-sm font-semibold">{day.label}</span>
                        {day.isToday && <span className="ml-2 text-xs font-medium text-primary">today</span>}
                    </div>
                    <div className="flex shrink-0 items-baseline gap-2">
                        {day.pendingPoints > 0 && (
                            <span className="text-xs text-amber-600 dark:text-amber-400 tabular-nums">
                                {day.pendingPoints} pending
                            </span>
                        )}
                        <span className="font-mono text-base font-bold tabular-nums">{day.totalPoints} pts</span>
                    </div>
                    <ChevronDown
                        className={cn(
                            "h-4 w-4 shrink-0 text-foreground/50 transition-transform",
                            expanded && "rotate-180",
                        )}
                    />
                </button>

                {showPendingHint && day.pendingPoints > 0 && (
                    <p className="px-4 pb-2 -mt-1 text-xs text-amber-600 dark:text-amber-400">
                        Pending points count after office review.
                    </p>
                )}

                {expanded && (
                    <div className="space-y-1 border-t px-3 pb-3 pt-2">
                        {day.jobs.map((job) => {
                            const key = `${day.day}:${job.jobId ?? "none"}`;
                            const open = openJobs.has(key);
                            return (
                                <div key={key} className="rounded-lg border p-2.5">
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            className="flex min-h-[44px] min-w-0 flex-1 items-center gap-2 text-left"
                                            onClick={() => toggleJob(key)}
                                        >
                                            <div className="min-w-0 flex-1">
                                                <div className="truncate text-sm font-medium">{job.label}</div>
                                                <div className="flex items-center gap-2 text-xs text-foreground/70">
                                                    <span className="font-mono font-semibold tabular-nums">
                                                        {job.points} pt{job.points === 1 ? "" : "s"}
                                                    </span>
                                                    <StatusChip status={job.status} />
                                                </div>
                                                {/* Y4 — "pending" is a workflow
                                                    word; this is what it means
                                                    for the tech's number. */}
                                                {redesign && job.status !== "rejected" && (
                                                    <div
                                                        className="text-[11px] text-foreground/60"
                                                        data-testid="points-job-state-note"
                                                    >
                                                        {
                                                            pointsJobStateCopy(
                                                                job.status === "approved" ? "earned" : "pending",
                                                            ).note
                                                        }
                                                    </div>
                                                )}
                                            </div>
                                            <ChevronDown
                                                className={cn(
                                                    "h-4 w-4 shrink-0 text-foreground/50 transition-transform",
                                                    open && "rotate-180",
                                                )}
                                            />
                                        </button>
                                        {job.jobId && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-11 shrink-0 px-2 text-xs"
                                                onClick={() => navigate(`/field/jobs/${job.jobId}`)}
                                            >
                                                <Wrench className="mr-1 h-3.5 w-3.5" /> Job
                                            </Button>
                                        )}
                                    </div>
                                    {open && <TaskLines job={job} />}
                                </div>
                            );
                        })}

                        {day.adjustments.map((a) => (
                            <div
                                key={a.id}
                                className={cn(
                                    "flex min-h-[44px] items-center justify-between gap-2 rounded-lg border border-dashed px-3 py-2 text-sm",
                                    a.status === "rejected" && "opacity-60",
                                )}
                            >
                                <span className="min-w-0 flex-1">
                                    <span className="font-medium">
                                        Adjustment: {a.deltaPoints > 0 ? "+" : ""}
                                        {a.deltaPoints}
                                    </span>
                                    {a.reason && <span className="text-foreground/70"> — {a.reason}</span>}
                                </span>
                                <StatusChip
                                    status={
                                        a.status === "approved"
                                            ? "approved"
                                            : a.status === "rejected"
                                              ? "rejected"
                                              : "pending"
                                    }
                                />
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

/**
 * Y5 — "Ask about a job's points".
 *
 * The whole point is that the tech never has to explain WHICH job: the app
 * sends it. This rides the existing field-flag channel (`POST /api/field/flag`
 * through the save-outbox), so it works with no signal and lands where the
 * office already watches — no new endpoint, no new inbox to forget about.
 * Category "other" is deliberate: the route leaves it unclassified so the
 * signal side reads the note rather than a wrong chip.
 */
function AskAboutPoints({
    jobs,
}: {
    jobs: { jobId: string | null; label: string; points: number; pending: boolean }[];
}) {
    const [open, setOpen] = useState(false);
    const [sending, setSending] = useState(false);

    async function ask(job: { jobId: string | null; label: string; points: number; pending: boolean }) {
        if (sending) return;
        setSending(true);
        try {
            await saveThroughOutbox({
                url: "/api/field/flag",
                method: "POST",
                body: {
                    jobId: job.jobId,
                    assetId: null,
                    category: "other",
                    note: pointsAskMessage({
                        jobLabel: job.label,
                        points: job.points,
                        state: job.pending ? "pending" : "earned",
                    }),
                    route: "/field/points",
                    appVersion: appVersion(),
                },
            });
            toast.success("Sent — the office has the exact job.");
            setOpen(false);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : "Couldn't send that — try again.");
        } finally {
            setSending(false);
        }
    }

    return (
        <Card data-testid="points-ask-card">
            <CardContent className="space-y-2 p-4">
                <p className="text-xs text-foreground/80">{POINTS_ASK_PROMPT}</p>
                <Button
                    variant="outline"
                    className="h-11 w-full gap-2"
                    onClick={() => setOpen((v) => !v)}
                    disabled={jobs.length === 0}
                >
                    <HelpCircle className="h-4 w-4" />
                    {jobs.length === 0 ? "No jobs in this period to ask about" : POINTS_ASK_LABEL}
                </Button>
                {open && (
                    <div className="space-y-1 pt-1" data-testid="points-ask-list">
                        {jobs.map((j) => (
                            <button
                                key={`${j.jobId ?? "none"}:${j.label}`}
                                type="button"
                                disabled={sending}
                                onClick={() => void ask(j)}
                                className="flex min-h-[44px] w-full items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-sm hover:bg-secondary"
                            >
                                <span className="min-w-0 flex-1 truncate">{j.label}</span>
                                <span className="shrink-0 font-mono text-xs tabular-nums text-foreground/70">
                                    {j.points} pt{j.points === 1 ? "" : "s"}
                                </span>
                            </button>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function MyPointsPage() {
    const navigate = useNavigate();
    // #950 Y2/Y3/Y5 — presentation only, behind the flag. Every number below
    // still comes from the same points engine and the same queries.
    const { enabled: redesign } = useFieldRedesign();
    // Teach cards (#1838): opening My points is using it.
    useTeachUsed("points.tech", true);

    // Same query key as the profile page so the cache is shared.
    const profileQ = useQuery({
        queryKey: ["me-profile"],
        queryFn: () => api.get<{ profile: MeProfilePay }>("/api/me/profile"),
    });
    const periodDays = profileQ.data?.profile.pay.payPeriodDays ?? 14;
    const dailyTarget = profileQ.data?.profile.pay.dailyPointsTarget ?? null;

    const periodOptions = useMemo(() => buildPeriodOptions(periodDays), [periodDays]);
    const [periodIdx, setPeriodIdx] = useState(0); // 0 = current, higher = further back
    const period = periodOptions[Math.min(periodIdx, periodOptions.length - 1)];

    const totalsQ = useQuery({
        queryKey: ["my-point-daily", period.start, period.end],
        queryFn: () =>
            api.get<{ days: ApiDayTotal[] }>(
                `/api/me/point-totals-by-day?periodStart=${period.start}&periodEnd=${period.end}`,
            ),
    });

    // Fetch every event of the period once (offset-paginate past 200) and
    // group client-side — level 2/3 expansions never refetch.
    const eventsQ = useQuery({
        queryKey: ["my-point-events", period.start, period.end],
        queryFn: async () => {
            const limit = 200;
            const all: ApiPointEvent[] = [];
            for (let offset = 0; ; offset += limit) {
                const r = await api.get<{ events: ApiPointEvent[]; total: number }>(
                    `/api/point-events?periodStart=${period.start}&periodEnd=${period.end}&dateBasis=work&limit=${limit}&offset=${offset}`,
                );
                all.push(...r.events);
                if (all.length >= r.total || r.events.length === 0) break;
            }
            return all;
        },
    });

    // Adjustments are few; fetch once and filter to the visible period.
    const adjustmentsQ = useQuery({
        queryKey: ["my-point-adjustments"],
        queryFn: () => api.get<{ adjustments: ApiAdjustment[] }>("/api/point-adjustments?limit=500"),
    });

    const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());

    const days = useMemo(
        () =>
            buildDayModel({
                period,
                totalsByDay: totalsQ.data?.days ?? [],
                events: eventsQ.data ?? [],
                adjustments: adjustmentsForPeriod(adjustmentsQ.data?.adjustments ?? [], period),
            }),
        [period, totalsQ.data, eventsQ.data, adjustmentsQ.data],
    );
    // Late points: earlier work approved after its period was paid. They pay in
    // THIS period's check (payroll carry rule), so they count in the total even
    // though their days are in an earlier period. Same number as the home card.
    const lateQ = useQuery({
        queryKey: ["my-point-summary", period.start, period.end],
        queryFn: () =>
            api.get<{ latePoints?: number | string }>(
                `/api/me/point-summary?periodStart=${period.start}&periodEnd=${period.end}`,
            ),
    });
    const latePoints = Number(lateQ.data?.latePoints ?? 0);
    const summary = useMemo(() => summarize(days, latePoints), [days, latePoints]);
    const bars = useMemo(() => buildBars(period, totalsQ.data?.days ?? []), [period, totalsQ.data]);

    if (profileQ.isLoading || (totalsQ.isLoading && !totalsQ.data)) return <PageSpinner />;

    const maxBar = Math.max(...bars.map((b) => b.points), dailyTarget ?? 0, 1);
    const firstPendingDay = days.find((d) => d.pendingPoints > 0)?.day ?? null;
    // Y3 — the bars know points; the day rows know whether the tech worked at
    // all and whether anything is pending. Join them by date rather than
    // re-deriving either.
    const dayByDate = new Map(days.map((d) => [d.day, d]));
    // Y4/Y5 — every job in the period, by name, so the ask sends the exact one.
    const askableJobs = days.flatMap((d) =>
        d.jobs.map((j) => ({ jobId: j.jobId, label: j.label, points: j.points, pending: j.status === "pending" })),
    );

    const jumpToDay = (day: string) => {
        setExpandedDays((prev) => new Set(prev).add(day));
        // Rendered synchronously above the fold or below — just scroll.
        requestAnimationFrame(() => {
            document.getElementById(`day-${day}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
        });
    };

    return (
        <div className="mx-auto max-w-2xl space-y-4 pb-16">
            {/* Teach cards (#1838). */}
            <TeachIntro featureKey="points.tech" when />
            <button
                onClick={() => navigate("/field")}
                className="-ml-1 flex items-center gap-1.5 px-1 py-1 text-sm text-shell-foreground/70 transition-colors hover:text-shell-foreground"
            >
                <ArrowLeft className="h-4 w-4" /> Home
            </button>

            {/* ── Level 1: period hero ── */}
            <Card>
                <CardContent className="space-y-4 p-4">
                    <div className="flex items-center justify-between gap-2">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-11 w-11 p-0"
                            disabled={periodIdx >= periodOptions.length - 1}
                            onClick={() => setPeriodIdx((i) => Math.min(i + 1, periodOptions.length - 1))}
                            aria-label="Previous pay period"
                        >
                            <ChevronLeft className="h-5 w-5" />
                        </Button>
                        <div className="text-center">
                            <div className="text-xs font-semibold uppercase tracking-wider text-foreground/60">
                                Pay period
                            </div>
                            <div className="text-sm font-semibold">
                                {period.label}
                                {period.isCurrent && (
                                    <span className="ml-1.5 text-xs font-medium text-primary">(current)</span>
                                )}
                            </div>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-11 w-11 p-0"
                            disabled={periodIdx === 0}
                            onClick={() => setPeriodIdx((i) => Math.max(i - 1, 0))}
                            aria-label="Next pay period"
                        >
                            <ChevronRight className="h-5 w-5" />
                        </Button>
                    </div>

                    <div className="text-center">
                        <div className="flex items-center justify-center gap-2">
                            <Star className="h-7 w-7 fill-yellow-500 text-yellow-500" />
                            <span className="font-mono text-5xl font-black tabular-nums">{summary.totalPoints}</span>
                            <span className="self-end pb-1 text-lg font-semibold text-foreground/70">pts</span>
                        </div>
                        <p className="mt-1 text-sm text-foreground/70">
                            {summary.approvedPoints} approved · {summary.pendingPoints} pending
                            {summary.latePoints > 0 && <> · {summary.latePoints} late from before</>}
                            {summary.adjustmentPoints !== 0 && (
                                <>
                                    {" · adjustments "}
                                    {summary.adjustmentPoints > 0 ? "+" : ""}
                                    {summary.adjustmentPoints}
                                </>
                            )}
                        </p>
                    </div>

                    {/* Y2 — what points ARE, on the screen a tech opens on day
                        one, instead of in a manual nobody reads. Money rule:
                        this explains points without naming a dollar figure. */}
                    {redesign && (
                        <div className="rounded-lg border bg-muted/40 p-3 text-xs" data-testid="points-explainer">
                            <p className="font-semibold">What points are</p>
                            <p className="mt-0.5 text-foreground/80">{pointsExplainer().what}</p>
                            <p className="mt-1.5 text-foreground/80">{pointsExplainer().pending}</p>
                        </div>
                    )}

                    {/* Mini bar chart — one bar per day; subtle daily-target line. */}
                    <div className="relative">
                        {dailyTarget != null && dailyTarget > 0 && (
                            <div
                                className="pointer-events-none absolute inset-x-0 border-t border-dashed border-foreground/25"
                                style={{ bottom: `${(dailyTarget / maxBar) * 100}%` }}
                                aria-hidden
                            />
                        )}
                        <div className={cn("flex items-end gap-1", redesign ? "h-24" : "h-16")}>
                            {bars.map((b) => {
                                // Y3 — a bar with no label is a shape, not a
                                // number. Every bar says its day and its value,
                                // a day with pending points is striped, and a
                                // day with no work says "off" rather than "0".
                                const dayRow = dayByDate.get(b.day);
                                const label = pointsBarLabel({
                                    dayLabel: shortWeekday(b.day),
                                    points: b.points,
                                    isToday: b.isToday,
                                    isFuture: b.isFuture,
                                    worked: !!dayRow && !dayRow.isZero,
                                });
                                const hasPending = (dayRow?.pendingPoints ?? 0) > 0;
                                return (
                                    <button
                                        key={b.day}
                                        type="button"
                                        className="group flex h-full flex-1 flex-col items-center justify-end gap-0.5"
                                        onClick={() => jumpToDay(b.day)}
                                        aria-label={`Jump to ${b.day}`}
                                        data-testid={redesign ? "points-bar" : undefined}
                                    >
                                        {redesign && (
                                            <span className="text-[10px] font-semibold tabular-nums leading-none text-foreground/70">
                                                {label.valueLabel}
                                            </span>
                                        )}
                                        <span className="flex w-full flex-1 items-end">
                                            <span
                                                className={cn(
                                                    "w-full rounded-sm transition-colors",
                                                    b.isToday
                                                        ? "bg-primary"
                                                        : b.isFuture
                                                          ? "bg-foreground/5"
                                                          : b.points > 0
                                                            ? "bg-yellow-500/70 group-active:bg-yellow-500"
                                                            : "bg-foreground/10",
                                                    redesign &&
                                                        hasPending &&
                                                        "[background-image:repeating-linear-gradient(45deg,transparent,transparent_3px,rgba(255,255,255,0.55)_3px,rgba(255,255,255,0.55)_6px)]",
                                                )}
                                                style={{ height: `${Math.max((b.points / maxBar) * 100, 4)}%` }}
                                            />
                                        </span>
                                        {redesign && (
                                            <span
                                                className={cn(
                                                    "text-[10px] leading-none",
                                                    label.isToday ? "font-bold text-primary" : "text-foreground/60",
                                                )}
                                            >
                                                {label.dayLabel}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* ── Level 2: days (newest first) ── */}
            <div className="space-y-2">
                <div className="flex items-center justify-end gap-2 px-1 text-xs text-shell-foreground/80">
                    Points sit on the day you did the work
                    <HowItWorks
                        title="How My Points days work"
                        testId="how-points-days"
                        steps={[
                            {
                                title: "Each line is on the day you did the work",
                                text: "Not the day it was typed in. That's the same day payroll pays it on.",
                                picture: "my-points-work-date.png",
                                alt: "A day on My Points with its job and points ringed",
                            },
                            {
                                title: "After-hours work says so",
                                text: 'A line marked "after hours 1.5×" pays one and a half times. It comes from when the job was booked, not when you started.',
                            },
                        ]}
                    />
                </div>
                {days.map((d) => (
                    <DayCard
                        key={d.day}
                        day={d}
                        expanded={expandedDays.has(d.day)}
                        onToggle={() =>
                            setExpandedDays((prev) => {
                                const next = new Set(prev);
                                if (next.has(d.day)) next.delete(d.day);
                                else next.add(d.day);
                                return next;
                            })
                        }
                        showPendingHint={d.day === firstPendingDay}
                        redesign={redesign}
                    />
                ))}
                {days.length === 0 && (
                    <p className="py-8 text-center text-sm text-shell-foreground/70">No days in this period yet.</p>
                )}
            </div>

            {redesign && <AskAboutPoints jobs={askableJobs} />}
        </div>
    );
}
